
import { ReactNode, createContext, useContext, useEffect } from 'react';
import { useLocation, useNavigationType, Location } from 'react-router-dom';
import { trackPageView, recordUserSession, updateSessionOnLogout, trackUserAction } from '@/services/analyticsService';
import { supabase } from '@/integrations/supabase/client';

type AnalyticsContextType = {
  trackAction: (actionType: string, details?: any) => Promise<void>;
};

const AnalyticsContext = createContext<AnalyticsContextType>({
  trackAction: async () => {},
});

export const useAnalytics = () => useContext(AnalyticsContext);

export const AnalyticsProvider = ({ children }: { children: ReactNode }) => {
  // Use try-catch to handle potential Router context issues
  let location: Location | null = null;
  try {
    location = useLocation();
  } catch (error) {
    console.error('AnalyticsProvider used outside Router context:', error);
  }
  
  // Track page views when location changes
  useEffect(() => {
    if (location) {
      trackPageView(location.pathname);
      
      // Record time spent on page before unload
      const handleBeforeUnload = () => {
        trackPageView('');
      };
      
      window.addEventListener('beforeunload', handleBeforeUnload);
      
      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
      };
    }
  }, [location?.pathname]);
  
  // Record user session on initial load and handle logout
  useEffect(() => {
    const checkAndRecordSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        recordUserSession();
      }
    };
    
    checkAndRecordSession();
    
    // Update session on auth state change
    const { data: authListener } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_IN') {
        recordUserSession();
      } else if (event === 'SIGNED_OUT') {
        updateSessionOnLogout();
      }
    });
    
    return () => {
      authListener.subscription.unsubscribe();
    };
  }, []);
  
  // Function to track user actions
  const trackAction = async (actionType: string, details?: any) => {
    await trackUserAction(actionType, details);
  };
  
  return (
    <AnalyticsContext.Provider value={{ trackAction }}>
      {children}
    </AnalyticsContext.Provider>
  );
};
